import React, { useEffect, useRef, useState } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { formatCurrencyCompact } from '../../../../utils/currency';
import { CELL_SAVE_DEBOUNCE_MS, MonthlyBudgetSlot } from '../../config';
import { notifyReadOnly } from '../../../../utils/supabaseErrors';
import './budget-total-row.scss';

interface BudgetTotalRowProps {
  monthlyBudget: MonthlyBudgetSlot[];
  forecastColumnTotals: number[];
  actualColumnTotals: number[];
  unallocatedSum: number;
  /**
   * Org-wide WON fee (quoted less probable), summed under the Won Fee column.
   * Aggregate rows' annual budgets fold in here; they have no probable part.
   */
  wonFeeSum: number;
  /** Org-wide probable fee. Aggregate rows contribute nothing — see index.tsx. */
  probableSum: number;
  onBudgetSaved: (year: number, month: number, newAmount: number) => void;
  /**
   * Render only the Totals row. Set while a status filter is active: the four
   * monthly rows below it are whole-business figures (the budget target is
   * org-wide), so they can never reconcile with a status-filtered list.
   */
  totalsOnly?: boolean;
  // FY25/26 view prepends a "Pre FY25/26" column; the total row mirrors it
  // with the org-wide sum of prior allocations. Later FYs hide both.
  showPriorColumn?: boolean;
  priorColumnTotal?: number;
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

interface BudgetCellProps {
  slot: MonthlyBudgetSlot;
  onSave: (year: number, month: number, amount: number) => void;
}

const BudgetCell: React.FC<BudgetCellProps> = ({ slot, onSave }) => {
  const supabase = useSupabaseClient();
  const [draft, setDraft] = useState<string>(slot.budget_amount > 0 ? String(slot.budget_amount) : '');
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedValue = useRef<number>(slot.budget_amount);

  // Sync draft from the upstream slot only on EXTERNAL changes. After our
  // own save the parent echoes back the value we just persisted — skipping
  // it here prevents in-flight keystrokes from being wiped.
  useEffect(() => {
    if (slot.budget_amount === lastSavedValue.current) return;
    setDraft(slot.budget_amount > 0 ? String(slot.budget_amount) : '');
    lastSavedValue.current = slot.budget_amount;
  }, [slot.budget_amount, slot.year, slot.month, slot.is_inherited]);

  const persist = async (raw: string) => {
    const parsed = raw.trim() === '' ? 0 : parseFloat(raw);
    if (isNaN(parsed)) {
      setSaveState('error');
      return;
    }
    if (parsed === lastSavedValue.current && !slot.is_inherited) {
      return;
    }

    setSaveState('saving');
    const { error } = await supabase.rpc('upsert_monthly_budget_target', {
      p_year: slot.year,
      p_month: slot.month,
      p_amount: parsed,
    });

    if (error) {
      console.error('Budget target save error:', error);
      notifyReadOnly(error);
      setSaveState('error');
      return;
    }

    lastSavedValue.current = parsed;
    setSaveState('saved');
    onSave(slot.year, slot.month, parsed);
    setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 1200);
  };

  const handleChange = (value: string) => {
    setDraft(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => persist(value), CELL_SAVE_DEBOUNCE_MS);
  };

  const handleBlur = () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    persist(draft);
  };

  return (
    <div className={`budget-cell ${slot.is_inherited ? 'is-inherited' : 'is-explicit'}`}>
      <input
        type="number"
        inputMode="decimal"
        step="0.01"
        className="budget-cell__input"
        value={draft}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={handleBlur}
        placeholder="—"
        title={slot.is_inherited ? 'Inherited from a previous month — type a new value to override' : 'Explicit budget for this month'}
      />
      {saveState === 'saving' && <span className="budget-cell__indicator budget-cell__indicator--saving">…</span>}
      {saveState === 'saved' && <span className="budget-cell__indicator budget-cell__indicator--saved">✓</span>}
      {saveState === 'error' && <span className="budget-cell__indicator budget-cell__indicator--error">!</span>}
    </div>
  );
};

const BudgetTotalRow: React.FC<BudgetTotalRowProps> = ({
  monthlyBudget,
  forecastColumnTotals,
  actualColumnTotals,
  unallocatedSum,
  wonFeeSum,
  probableSum,
  onBudgetSaved,
  totalsOnly = false,
  showPriorColumn = false,
  priorColumnTotal = 0,
}) => {
  // Row-level totals shown inside the LHS label area, right-aligned at the
  // band edge where the month columns begin.
  const allocatedFeeSum = forecastColumnTotals.reduce((acc, n) => acc + n, 0);
  const actualSum = actualColumnTotals.reduce((acc, n) => acc + n, 0);
  const targetSum = monthlyBudget.reduce((acc, s) => acc + s.budget_amount, 0);
  const varianceSum = monthlyBudget.reduce((acc, s, idx) => {
    const actual = actualColumnTotals[idx] ?? 0;
    if (actual <= 0) return acc;
    return acc + (actual - s.budget_amount);
  }, 0);

  return (
    <div className="budget-total-row">
      {/* Column totals. These three figures belong to the Probable, Won Fee
          and Unallocated COLUMNS, so they sit directly under them and nowhere
          else. Keeping them on their own row is what lets every row below
          put its single monthly total in one consistent place — they were
          previously crammed onto the Allocated Fees row alongside a figure
          that means something entirely different.

          The month cells are deliberately blank: a fee total has no monthly
          breakdown, and filling them with the forecast sums (which do) would
          invite reading across a row that doesn't add up. */}
      <div className="budget-total-row__row budget-total-row__row--totals">
        <div className="budget-total-row__label">
          <span className="budget-total-row__label-text">Totals</span>
          <span
            className={`budget-total-row__label-probable${probableSum > 0 ? ' is-probable' : ''}`}
            title="Fee quoted but not yet awarded, across every row. Excluded from Won Fee and Unallocated."
          >
            {probableSum > 0 ? formatCurrencyCompact(probableSum) : '—'}
          </span>
          <span
            className="budget-total-row__label-fee"
            title="Fee awarded across every row — total quoted less anything still probable"
          >
            {formatCurrencyCompact(wonFeeSum)}
          </span>
        </div>
        {showPriorColumn && <div className="budget-total-row__cell" />}
        {forecastColumnTotals.map((_, idx) => (
          <div key={`tot-${idx}`} className="budget-total-row__cell" />
        ))}
        <div
          className="budget-total-row__cell budget-total-row__cell--readout budget-total-row__cell--unalloc"
          title="Won fee across every row that is not yet allocated to a month"
        >
          <strong>{formatCurrencyCompact(unallocatedSum)}</strong>
        </div>
      </div>

      {!totalsOnly && (
        <>
          <div className="budget-total-row__row budget-total-row__row--forecast-sum">
            <div className="budget-total-row__label">
              <span className="budget-total-row__label-text">Allocated Fees</span>
              <span className="budget-total-row__label-value">
                <strong>{formatCurrencyCompact(allocatedFeeSum)}</strong>
              </span>
            </div>
            {showPriorColumn && (
              <div className="budget-total-row__cell budget-total-row__cell--readout">
                {priorColumnTotal > 0 ? formatCurrencyCompact(priorColumnTotal) : '—'}
              </div>
            )}
            {forecastColumnTotals.map((t, idx) => (
              <div key={`fc-${idx}`} className="budget-total-row__cell budget-total-row__cell--readout">
                {t > 0 ? formatCurrencyCompact(t) : '—'}
              </div>
            ))}
            <div className="budget-total-row__cell budget-total-row__cell--readout budget-total-row__cell--unalloc" />
          </div>

          <div className="budget-total-row__row budget-total-row__row--actual-sum">
            <div className="budget-total-row__label">
              <span className="budget-total-row__label-text">Actual Invoiced (FY)</span>
              <span className="budget-total-row__label-value">
                {actualSum > 0 ? formatCurrencyCompact(actualSum) : '—'}
              </span>
            </div>
            {showPriorColumn && (
              <div className="budget-total-row__cell budget-total-row__cell--readout">—</div>
            )}
            {actualColumnTotals.map((t, idx) => (
              <div key={`ac-${idx}`} className="budget-total-row__cell budget-total-row__cell--readout">
                {t > 0 ? formatCurrencyCompact(t) : '—'}
              </div>
            ))}
            <div className="budget-total-row__cell budget-total-row__cell--readout budget-total-row__cell--unalloc" />
          </div>

          <div className="budget-total-row__row budget-total-row__row--target">
            <div className="budget-total-row__label">
              <span className="budget-total-row__label-text">Monthly Budget Target</span>
              <span className="budget-total-row__label-value">
                {targetSum > 0 ? formatCurrencyCompact(targetSum) : '—'}
              </span>
            </div>
            {showPriorColumn && (
              <div className="budget-total-row__cell budget-total-row__cell--readout">—</div>
            )}
            {monthlyBudget.map((slot) => (
              <div key={`bt-${slot.year}-${slot.month}`} className="budget-total-row__cell">
                <BudgetCell slot={slot} onSave={onBudgetSaved} />
              </div>
            ))}
            <div className="budget-total-row__cell budget-total-row__cell--readout budget-total-row__cell--unalloc" />
          </div>

          <div className="budget-total-row__row budget-total-row__row--variance">
            <div className="budget-total-row__label">
              <span className="budget-total-row__label-text">Variance (Invoiced − Target)</span>
              <span
                className={`budget-total-row__label-value ${
                  varianceSum === 0 ? '' : varianceSum > 0 ? 'is-positive' : 'is-negative'
                }`}
              >
                {actualSum > 0 ? formatCurrencyCompact(varianceSum) : '—'}
              </span>
            </div>
            {showPriorColumn && (
              <div className="budget-total-row__cell budget-total-row__cell--readout">—</div>
            )}
            {monthlyBudget.map((slot, idx) => {
              const actual = actualColumnTotals[idx] ?? 0;
              if (actual <= 0) {
                return (
                  <div key={`v-${slot.year}-${slot.month}`} className="budget-total-row__cell budget-total-row__cell--readout">
                    —
                  </div>
                );
              }
              const variance = actual - slot.budget_amount;
              const cls = variance === 0 ? '' : variance > 0 ? 'is-positive' : 'is-negative';
              return (
                <div key={`v-${slot.year}-${slot.month}`} className={`budget-total-row__cell budget-total-row__cell--readout ${cls}`}>
                  {formatCurrencyCompact(variance)}
                </div>
              );
            })}
            <div className="budget-total-row__cell budget-total-row__cell--readout budget-total-row__cell--unalloc" />
          </div>
        </>
      )}
    </div>
  );
};

export default BudgetTotalRow;
